/**
 * The right column (§5.13): « Intention observée, tour par tour » ABOVE the current turn's
 * card. The score is the agent's `promptScore.score` and nothing else — no axis label, no
 * recomputation, and an absent score is never printed as 0.
 */

import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import { TARGET_CATEGORIES, type ConversationAnalysisStatus, type ConversationTurn } from '@traaker/shared';
import { CATEGORY_CHIP_COLOR } from '../../../utils/targetColors';
import {
  BUCKETS,
  BUCKET_COLOR,
  bucketSummary,
  competitorsInTurn,
  isOwnSource,
  scoreColor,
  sourceDomainAt,
  turnBucketView,
  turnScoreView,
  type TurnScoreView,
} from './conversationView';
import { dropReason } from './turnText';

const PANEL_SX = {
  p: 2.25, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2,
  boxShadow: '0 1px 2px rgba(23,21,29,.05), 0 4px 16px rgba(23,21,29,.05)',
} as const;

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <Typography component="div" sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}>
      {children}
    </Typography>
  );
}

const UNSCORED_TIP = {
  not_named: 'conversations.view.scoreNotNamedTip',
  not_ranked: 'conversations.view.scoreNotRankedTip',
  judge_failed: 'conversations.view.scoreJudgeFailedTip',
} as const;
const UNSCORED_NOTE = {
  not_named: 'conversations.view.turnNotNamed',
  not_ranked: 'conversations.view.turnNotRanked',
  judge_failed: 'conversations.view.turnJudgeFailed',
} as const;

const Dot = ({ color }: { color: string }) => <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flex: 'none', display: 'inline-block' }} />;

/** The score chip of a list row: the number, « — », or a muted state word. */
export function ScoreChip({ view }: { view: TurnScoreView }) {
  const { t } = useTranslation();
  const base = { fontSize: 11.5, fontWeight: 700, px: 1, py: 0.25, borderRadius: 999, whiteSpace: 'nowrap' } as const;
  if (view.kind === 'score') {
    const c = scoreColor(view.score);
    return <Box component="span" sx={{ ...base, bgcolor: c.bg, color: c.fg }}>{view.score}</Box>;
  }
  if (view.kind === 'unscored') {
    return (
      <Tooltip title={t(UNSCORED_TIP[view.reason])}>
        <Box component="span" sx={{ ...base, bgcolor: '#F3F4F6', color: 'text.secondary' }}>—</Box>
      </Tooltip>
    );
  }
  return (
    <Box component="span" sx={{ ...base, bgcolor: '#F3F4F6', color: 'text.secondary', fontStyle: 'italic', fontWeight: 600 }}>
      {view.kind === 'pending' ? t('conversations.view.scorePending') : t('conversations.view.unanalyzed')}
    </Box>
  );
}

function bucketText(t: (k: string) => string, turn: ConversationTurn, status?: ConversationAnalysisStatus): { label: string; color: string } {
  const b = turnBucketView(turn, status);
  if (b.kind === 'bucket') return { label: t(`conversations.view.bucket_${b.bucket}`), color: BUCKET_COLOR[b.bucket] };
  return { label: b.kind === 'pending' ? t('conversations.view.intentPending') : t('conversations.view.intentUnanalyzed'), color: '#9CA3AF' };
}

export function IntentsPanel({
  turns,
  activeTurn,
  cellAnalysisStatus,
  onPick,
}: {
  turns: ConversationTurn[];
  activeTurn: number;
  cellAnalysisStatus?: ConversationAnalysisStatus;
  onPick: (turn: number) => void;
}) {
  const { t } = useTranslation();
  const summary = bucketSummary(turns);
  return (
    <Box sx={PANEL_SX}>
      <Eyebrow>{t('conversations.view.intentsTitle')}</Eyebrow>
      <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: 1 }}>
        {turns.map((turn, i) => {
          const b = bucketText(t, turn, cellAnalysisStatus);
          const act = turn.turn === activeTurn;
          return (
            <Box
              component="li"
              key={turn.turn}
              onClick={() => onPick(turn.turn)}
              sx={{
                display: 'grid', gridTemplateColumns: '30px 1fr auto', gap: 1, alignItems: 'center', px: 0.75, py: 0.9,
                borderTop: i === 0 ? 0 : '1px solid #F0EEF4', borderRadius: '6px', cursor: 'pointer', fontSize: 13,
                bgcolor: act ? '#F3EEFF' : 'transparent', boxShadow: act ? 'inset 3px 0 #7C3AED' : 'none',
                '&:hover': { bgcolor: act ? '#F3EEFF' : '#F8F7FC' },
              }}
            >
              <Box sx={{ color: 'text.secondary', fontWeight: 600, fontSize: 12 }}>{t('conversations.view.turnShort', { n: turn.turn })}</Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9, fontStyle: b.color === '#9CA3AF' ? 'italic' : 'normal', color: b.color === '#9CA3AF' ? 'text.secondary' : 'text.primary' }}>
                <Dot color={b.color} />{b.label}
              </Box>
              <ScoreChip view={turnScoreView(turn, cellAnalysisStatus)} />
            </Box>
          );
        })}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1, mt: 1.75 }}>
        {BUCKETS.map((b) => {
          const s = summary[b];
          return (
            <Box key={b} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '10px', px: 1.25, py: 1, opacity: s.turns ? 1 : 0.45 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
                <Dot color={BUCKET_COLOR[b]} />{t(`conversations.view.bucket_${b}`)}
              </Box>
              <Box sx={{ mt: 0.25, fontSize: 18, fontWeight: 700 }}>
                {s.mean != null ? (
                  <>
                    {s.mean}
                    <Box component="span" sx={{ fontSize: 11.5, color: 'text.secondary', fontWeight: 500 }}>
                      {' · '}{t('conversations.view.bucketTurns', { count: s.turns })}
                    </Box>
                  </>
                ) : (
                  <Box component="span" sx={{ fontSize: 11.5, color: 'text.secondary', fontWeight: 500 }}>
                    {s.turns ? t('conversations.view.bucketNoScore') : t('conversations.view.bucketNoTurn')}
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function Chip({ children, tone = 'plain' }: { children: React.ReactNode; tone?: 'plain' | 'ok' | 'ko' | 'pos' | 'neg' | 'neu' | 'pend' }) {
  const tones = {
    plain: { border: '1px solid', borderColor: 'divider', color: 'text.secondary', bgcolor: '#fff' },
    ok: { border: '1px solid #86EFAC', color: '#15803D', bgcolor: '#F0FDF4' },
    ko: { border: '1px solid #FCA5A5', color: '#B91C1C', bgcolor: '#FEF2F2' },
    pos: { border: '1px solid transparent', color: '#00A98A', bgcolor: '#00DDB320' },
    neg: { border: '1px solid transparent', color: '#E03131', bgcolor: '#FF444420' },
    neu: { border: '1px solid transparent', color: '#6B7DA3', bgcolor: '#8B9DC320' },
    pend: { border: '1px dashed', borderColor: 'divider', color: 'text.secondary', bgcolor: '#fff', fontStyle: 'italic' },
  }[tone];
  return <Box component="span" sx={{ fontSize: 11.5, fontWeight: 600, px: 1.1, py: 0.35, borderRadius: 999, ...tones }}>{children}</Box>;
}

export function TurnCard({
  turns,
  activeTurn,
  cellAnalysisStatus,
  brand,
  dropOutTurn,
  onNav,
}: {
  turns: ConversationTurn[];
  activeTurn: number;
  cellAnalysisStatus?: ConversationAnalysisStatus;
  brand: { name: string; aliases: string[] };
  /** The run's drop-out turn — the same one the phone's band marks. */
  dropOutTurn: number | null;
  onNav: (turn: number) => void;
}) {
  const { t } = useTranslation();
  const i = Math.max(0, turns.findIndex((x) => x.turn === activeTurn));
  const turn = turns[i];
  if (!turn) return null;
  const prev = turns[i - 1];
  const b = bucketText(t, turn, cellAnalysisStatus);
  const view = turnScoreView(turn, cellAnalysisStatus);
  const ps = turn.promptScore;
  const n = (turn.sourceLinks?.length || turn.sources?.length) ?? 0;
  const ownSources = Array.from({ length: n }, (_, k) => k).filter((k) => isOwnSource(turn, k));
  const ownDomain = ownSources.length > 0 ? sourceDomainAt(turn, ownSources[0]) : null;
  const rivals = competitorsInTurn(turn, brand);
  const isDrop = dropOutTurn === turn.turn;
  const cats = (turn.citedCategories ?? []).map((ci) => TARGET_CATEGORIES[ci]).filter((c) => c && c !== 'offre');

  const big = view.kind === 'score'
    ? <Typography sx={{ fontFamily: '"Inter Tight", Inter, sans-serif', fontSize: 40, fontWeight: 700, lineHeight: 1, letterSpacing: '-0.02em', color: scoreColor(view.score).fg }}>{view.score}</Typography>
    : <Typography sx={{ fontSize: 40, fontWeight: 700, lineHeight: 1, color: 'text.disabled' }}>{view.kind === 'unscored' ? '—' : '…'}</Typography>;
  const bigNote = view.kind === 'unscored'
    ? t(UNSCORED_NOTE[view.reason])
    : view.kind === 'pending' ? t('conversations.view.turnPending')
      : view.kind === 'unanalyzed' ? t('conversations.view.turnUnanalyzed') : null;

  return (
    <Box sx={PANEL_SX}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Eyebrow>
          {t('conversations.view.turnKicker', { n: turn.turn, total: turns.length })}
          {' · '}
          <Box component="span" sx={{ color: 'text.primary', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <Dot color={b.color} />{b.label}
          </Box>
        </Eyebrow>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size="small" aria-label={t('conversations.view.prevTurn')} disabled={i <= 0} onClick={() => onNav(turns[i - 1].turn)} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '7px', width: 28, height: 28 }}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" aria-label={t('conversations.view.nextTurn')} disabled={i >= turns.length - 1} onClick={() => onNav(turns[i + 1].turn)} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '7px', width: 28, height: 28 }}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, mt: 1.25 }}>
        {big}
        {bigNote && <Typography sx={{ fontSize: 12.5, color: 'text.secondary', lineHeight: 1.35 }}>{bigNote}</Typography>}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
        {ps ? (
          <>
            <Chip tone={ps.named ? 'ok' : 'ko'}>{ps.named ? t('conversations.view.chipNamed') : t('conversations.view.chipNotNamed')}</Chip>
            <Chip tone={ps.brandIsSource ? 'ok' : 'plain'}>
              {ps.brandIsSource
                ? ownSources.length > 1 ? t('conversations.view.chipSourceMany', { count: ownSources.length }) : t('conversations.view.chipSource')
                : t('conversations.view.chipNotSource')}
            </Chip>
            {ps.position != null && (
              <Chip>{ps.total ? t('conversations.view.chipPosition', { position: ps.position, total: ps.total }) : t('conversations.view.chipPositionOnly', { position: ps.position })}</Chip>
            )}
            {ps.recommended != null && ps.unscoredReason !== 'judge_failed' && (
              <Chip tone={ps.recommended ? 'ok' : 'plain'}>{ps.recommended ? t('conversations.view.chipRecommended') : t('conversations.view.chipNotRecommended')}</Chip>
            )}
            {ps.sentiment && (
              <Chip tone={ps.sentiment === 'positive' ? 'pos' : ps.sentiment === 'negative' ? 'neg' : 'neu'}>{t(`conversations.view.chipSentiment_${ps.sentiment}`)}</Chip>
            )}
            {ps.alignment && <Chip>{t(`conversations.view.chipAlignment_${ps.alignment}`)}</Chip>}
          </>
        ) : (
          <Chip tone="pend">{view.kind === 'pending' ? t('conversations.view.scorePending') : t('conversations.view.unanalyzed')}</Chip>
        )}
        {turn.eliminated === true && <Chip tone="ko">{t('conversations.view.chipEliminated')}</Chip>}
        {cats.map((c) => {
          const cc = CATEGORY_CHIP_COLOR[c];
          return (
            <Box key={c} component="span" sx={{ fontSize: 11.5, fontWeight: 600, px: 1.1, py: 0.35, borderRadius: 999, bgcolor: cc?.bg ?? '#F3F4F6', color: cc?.fg ?? 'text.secondary' }}>
              {t(`cibles.tab.${c}`)}
            </Box>
          );
        })}
      </Box>

      {ps?.alignmentReason && (ps.alignment === 'weak' || ps.alignment === 'misaligned' || ps.alignment === 'partial') && (
        <Typography sx={{ mt: 1.25, fontSize: 12, color: 'text.secondary', lineHeight: 1.5 }}>{ps.alignmentReason}</Typography>
      )}

      {isDrop && (
        <Box sx={{ mt: 1.5, px: 1.25, py: 1, borderRadius: '8px', bgcolor: '#FEF3C7', color: '#78350F', fontSize: 12 }}>
          <b>{t('conversations.view.dropHere')}</b> {dropReason(turn, prev, t)}
        </Box>
      )}

      <Box sx={{ mt: 1.5 }}>
        <Eyebrow>{t('conversations.view.competitorsInTurn')}</Eyebrow>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>
          {rivals.length ? rivals.join(' · ') : t('conversations.view.none')}
        </Typography>
      </Box>
      <Box sx={{ mt: 1.25 }}>
        <Eyebrow>{t('conversations.view.sourcesTitle')}</Eyebrow>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>
          {t('conversations.view.sourcesCount', { count: n })}
          {ownDomain && (
            <>
              {', '}
              {t('conversations.view.sourcesOwnPrefix')}{' '}
              <Box component="b" sx={{ color: 'primary.dark' }}>{ownDomain}</Box>
            </>
          )}
        </Typography>
      </Box>
    </Box>
  );
}
